import React, { useMemo } from 'react'
import copy from 'copy-to-clipboard'
import {
  isTopLevelFrameElement,
  SketchElement,
  SketchLayerCSSProperties,
} from '../../../inspector'
import { ElementIcon } from '../../../shares/FrameView/components/FrameDetailInspectorContextMenu/ElementIcon'
import { useGenerateLayerCSS } from '../../../shares/FrameView/components/FrameDetailInspector/useGenerateLayerCSS'
import { useExportModal } from '../../../shares/FrameView/components/FrameDetailInspector/sections/Exports/ExportModal'
import { useDownloadLayerAssets } from '../../../shares/FrameView/components/FrameDetailInspector/sections/Exports/useDownloadLayerAssets'
import { useShowExportErrorIfNeeded } from '../../../shares/FrameView/components/FrameDetailInspector/sections/Exports'
import { Dropdown } from '@sketch/components'
import { FlatTreeNode } from './types'
import { VirtualItem } from '@tanstack/react-virtual'
import { useToast } from '@sketch/toasts'
import { useVersioning } from '../../../versioning'

import { ReactComponent as CopyCSSIcon } from '@sketch/icons/img-markdown-code-inline-17'
import { ReactComponent as CopyLinkIcon } from '@sketch/icons/img-markdown-link-share-17'
import { ReactComponent as ExportIcon } from '@sketch/icons/img-arrow-down-circle-17'
import { ReactComponent as GoToLayerIcon } from '@sketch/icons/arrow-right-circle-semifilled-17'

import * as S from './LayersTreeItem.styles'
import {
  useCreateLayerLink,
  useShouldEnableCopyLinkToLayer,
} from './useCreateLayerLink'
import { VersionFragment } from '@sketch/gql-types'
import { usePreview } from '../../../shares/views/PreviewView/PreviewContext'

type LayersTreeItemProps = {
  layer: FlatTreeNode
  isSelected: boolean
  isHovered: boolean
  onSelect: (sketchElement: SketchElement) => void
  onHover: (sketchElement: SketchElement | null) => void
  onToggleCollapse: (identifier: bigint) => void
  goToLayer: (sketchElement: SketchElement) => void
  virtualItem: VirtualItem
}

export function LayersTreeItem({
  layer,
  isSelected,
  isHovered,
  onSelect,
  onHover,
  onToggleCollapse,
  goToLayer,
  virtualItem,
}: LayersTreeItemProps) {
  const [isDropdownOpen, setIsDropdownOpen] = React.useState(false)
  // Different from isHovered prop which represents the hover state of the inspector
  // ( isHovered = hover of canvas or tree item | isHoveringTreeItem = hover of the tree item itself)
  const [isHoveringTreeItem, setIsHoveringTreeItem] = React.useState(false)
  const layerTreeItemRef = React.useRef<HTMLLIElement>(null)
  const lastMouseTargetRef = React.useRef<EventTarget | null>(null)

  const { sketchElement, level, hasChildren, isCollapsed, isLayerMasked } =
    layer

  const handleMouseEnter = () => {
    setIsHoveringTreeItem(true)
    onHover(sketchElement)
  }

  const isLoadingFragment =
    isTopLevelFrameElement(sketchElement) && sketchElement.hasFragmentPending

  const handleMouseLeave = () => {
    setIsHoveringTreeItem(false)
    onHover(null)
  }

  const handleToggleCollapse = (event: React.MouseEvent) => {
    event.stopPropagation()
    onToggleCollapse(sketchElement.identifier)
  }

  // Track mouse movements globally only when dropdown is open
  React.useEffect(() => {
    if (!isDropdownOpen) return

    const handleGlobalMouseMove = (event: MouseEvent) => {
      lastMouseTargetRef.current = event.target
    }

    document.addEventListener('mousemove', handleGlobalMouseMove)
    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove)
    }
  }, [isDropdownOpen])

  const handleDropdownToggle = (visible: boolean) => {
    setIsDropdownOpen(visible)

    // When the dropdown closes, we can't be sure what triggered the close,
    // it could be a click outside the dropdown, a click on an action within it,
    // or a click on the tree item that owns it.
    //
    // Because the dropdown menu is rendered inside the tree item, hovering over the menu
    // also counts as hovering over the row. However, once the dropdown closes,
    // we need to re-evaluate whether the mouse is still hovering the row or not.
    if (!visible && layerTreeItemRef.current && lastMouseTargetRef.current) {
      const isMouseOverRow =
        lastMouseTargetRef.current instanceof Node &&
        layerTreeItemRef.current.contains(lastMouseTargetRef.current)

      setIsHoveringTreeItem(isMouseOverRow)
    }
  }

  const verticalLines = useMemo(() => {
    return Array.from({ length: level }).map((_, index) => (
      <S.VerticalLine key={index} level={index} />
    ))
  }, [level])

  return (
    <S.VirtualLayerItem
      ref={layerTreeItemRef}
      role="treeitem"
      aria-selected={isSelected}
      // Keep the hover highlight when the dropdown is open
      $isHovered={isHovered || isDropdownOpen}
      $level={level}
      onClick={() => onSelect(sketchElement)}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      style={{
        height: `${virtualItem.size}px`,
        transform: `translateY(${virtualItem.start}px)`,
      }}
    >
      {verticalLines}
      <ItemIcon
        // don't change the icon while dropdown is open to avoid flickering the icon
        // while the focus should be on the dropdown.
        isHoveringTreeItem={isHoveringTreeItem || isDropdownOpen}
        hasChildren={hasChildren}
        isCollapsed={isCollapsed}
        handleToggleCollapse={handleToggleCollapse}
        sketchElement={sketchElement}
        isLayerMasked={isLayerMasked}
        isLoading={isLoadingFragment}
      />
      <S.LayerName>{sketchElement.name}</S.LayerName>
      {!isLoadingFragment && (
        // Prevent actions from selecting the
        <div onClick={e => e.stopPropagation()}>
          <Dropdown
            toggle={
              <S.EllipsisButton
                $isVisible={isHoveringTreeItem || isDropdownOpen}
                aria-label="Layer options"
              >
                <S.EllipsisIcon />
              </S.EllipsisButton>
            }
            placement="top-start"
            usePortal={true}
            onToggle={handleDropdownToggle}
          >
            <LayerItemActions
              sketchElement={sketchElement}
              onGoToLayer={goToLayer}
              onSelect={onSelect}
            />
          </Dropdown>
        </div>
      )}
    </S.VirtualLayerItem>
  )
}

type ItemIconProps = {
  isHoveringTreeItem: boolean
  hasChildren: boolean
  isCollapsed: boolean
  handleToggleCollapse: (event: React.MouseEvent) => void
  sketchElement: SketchElement
  isLayerMasked: boolean
  isLoading?: boolean
}

function ItemIcon({
  isHoveringTreeItem,
  hasChildren,
  isCollapsed,
  handleToggleCollapse,
  sketchElement,
  isLayerMasked,
  isLoading,
}: ItemIconProps) {
  if (isLoading) {
    return (
      <S.SpinnerContainer>
        <S.LoadingSpinner />
      </S.SpinnerContainer>
    )
  }
  let content = (
    <ElementIcon
      elementType={sketchElement.type}
      isLayerMasked={isLayerMasked}
      isLayerExportable={false}
    />
  )

  if (isHoveringTreeItem && hasChildren) {
    content = (
      <S.ToggleButton onClick={handleToggleCollapse}>
        <S.ToggleIcon $isCollapsed={isCollapsed} />
      </S.ToggleButton>
    )
  }

  return <S.ItemIconContainer>{content}</S.ItemIconContainer>
}

type DownloadAllActionProps = {
  sketchElement: SketchElement
  currentVersion: VersionFragment
}

function DownloadAllAction({
  sketchElement,
  currentVersion,
}: DownloadAllActionProps) {
  // Download All functionality
  const { renderAssets, errorType } = useDownloadLayerAssets({
    layerOrArtboardId: sketchElement.exportId || '',
    currentVersion: currentVersion,
  })
  useShowExportErrorIfNeeded(errorType)

  const handleExportAllClick = () => {
    if (!sketchElement.exportId) return
    renderAssets()
  }

  if (!sketchElement.isExportable) return null

  return (
    <S.StyledDropdownItem onClick={handleExportAllClick}>
      <S.MenuIcon as={ExportIcon} />
      Export
    </S.StyledDropdownItem>
  )
}

type LayerItemActionsProps = {
  sketchElement: SketchElement
  onGoToLayer: (sketchElement: SketchElement) => void
  onSelect: (sketchElement: SketchElement) => void
}

function LayerItemActions({
  sketchElement,
  onGoToLayer,
  onSelect,
}: LayerItemActionsProps) {
  const { showToast } = useToast()
  const { openExportModal } = useExportModal()
  const { currentVersion } = useVersioning()

  // Previews don't expose the `currentVersion` of a document, and so we don't have a document
  // identifier to pass to the export mutation. In this case we don't show the option to export
  // any predefined exports on the selected item.
  const previewContext = usePreview()
  const isPreview = Boolean(previewContext)

  const layerCSS = useGenerateLayerCSS(
    sketchElement as SketchLayerCSSProperties
  )
  const createLayerLink = useCreateLayerLink()

  const handleCopyCSSClick = () => {
    if (!layerCSS.css.length) return
    copy(layerCSS.toString())
    showToast('CSS copied')
  }

  const handleCopyLinkClick = () => {
    const url = createLayerLink(sketchElement.layerPathHash)
    if (url) {
      copy(url)
      showToast('Link copied')
    }
  }

  const handleExportAsClick = () => {
    openExportModal(sketchElement.identifier, sketchElement.name)
  }

  const shouldEnableCopyLink = useShouldEnableCopyLinkToLayer()

  const handleGoToLayerClick = () => {
    onSelect(sketchElement)
    onGoToLayer(sketchElement)
  }

  return (
    <>
      <Dropdown.Header> Layer </Dropdown.Header>
      <S.StyledDropdownItem onClick={handleGoToLayerClick}>
        <S.MenuIcon as={GoToLayerIcon} />
        Go to Layer
      </S.StyledDropdownItem>
      {shouldEnableCopyLink && (
        <S.StyledDropdownItem onClick={handleCopyLinkClick}>
          <S.MenuIcon as={CopyLinkIcon} />
          Copy Link
        </S.StyledDropdownItem>
      )}
      {layerCSS.css.length > 0 && (
        <S.StyledDropdownItem onClick={handleCopyCSSClick}>
          <S.MenuIcon as={CopyCSSIcon} />
          Copy CSS
        </S.StyledDropdownItem>
      )}
      {!isPreview && (
        <DownloadAllAction
          sketchElement={sketchElement}
          currentVersion={currentVersion!}
        />
      )}
      <S.StyledDropdownItem onClick={handleExportAsClick}>
        <S.MenuIcon as={ExportIcon} />
        Export as…
      </S.StyledDropdownItem>
    </>
  )
}
